import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Image as ImageIcon, RotateCcw, Upload } from "lucide-react";
import { FormEvent, useEffect, useMemo, useState } from "react";

import { ErrorState, LoadingState, MutationMessage, PageHeader, Section, StatusBadge } from "../account/components";
import { api, formatDate } from "../api";

type BrandAsset = {
  source: "DEFAULT" | "DATABASE";
  contentType: "image/x-icon" | "image/png";
  size: number;
  revision: string;
  assetUrl: string;
  updatedAt: string | null;
  version: number;
};

export function BrandingSettingsPage() {
  const client = useQueryClient();
  const [file, setFile] = useState<File | null>(null);
  const state = useQuery({
    queryKey: ["operator-branding"],
    queryFn: () => api<BrandAsset>("/api/v1/operator/super-admin/branding"),
  });
  const preview = useMemo(() => file ? URL.createObjectURL(file) : null, [file]);
  useEffect(() => () => { if (preview) URL.revokeObjectURL(preview); }, [preview]);
  const refresh = (asset: BrandAsset) => {
    setFile(null);
    updateDocumentFavicon(asset.assetUrl);
    client.setQueryData(["operator-branding"], asset);
  };
  const upload = useMutation({
    mutationFn: () => {
      if (!file || !state.data) throw new Error("请选择图标文件");
      const body = new FormData();
      body.append("file", file);
      body.append("expectedVersion", String(state.data.version));
      return api<BrandAsset>("/api/v1/operator/super-admin/branding", { method: "PUT", body });
    },
    onSuccess: refresh,
  });
  const reset = useMutation({
    mutationFn: () => api<BrandAsset>(`/api/v1/operator/super-admin/branding?expectedVersion=${state.data?.version ?? -1}`, { method: "DELETE" }),
    onSuccess: refresh,
  });

  return <>
    <PageHeader eyebrow="SUPER ADMIN" title="品牌设置" description="管理浏览器标签、书签和快捷方式使用的站点图标。" action={<StatusBadge tone={state.data?.source === "DATABASE" ? "good" : "neutral"}><ImageIcon />{state.data?.source === "DATABASE" ? "自定义图标" : "默认图标"}</StatusBadge>} />
    {state.isPending && <LoadingState label="正在读取品牌设置" />}
    {state.error && <ErrorState error={state.error} retry={() => void state.refetch()} />}
    {state.data && <Section title="站点图标" description="支持 ICO 或 PNG，文件不超过 512 KiB，图片边长为 16 到 1024 像素。">
      <div className="branding-workspace">
        <div className="favicon-preview"><img src={preview ?? state.data.assetUrl} alt="当前站点图标预览" /><span>{file ? "待上传预览" : state.data.source === "DATABASE" ? "当前自定义图标" : "EvilEye 默认图标"}</span></div>
        <form className="branding-form" onSubmit={(event: FormEvent) => { event.preventDefault(); upload.mutate(); }}>
          <dl className="branding-facts"><div><dt>格式</dt><dd>{file?.type || state.data.contentType}</dd></div><div><dt>大小</dt><dd>{formatBytes(file?.size ?? state.data.size)}</dd></div><div><dt>更新时间</dt><dd>{formatDate(state.data.updatedAt)}</dd></div></dl>
          <label><span>选择新图标</span><input accept=".ico,.png,image/x-icon,image/vnd.microsoft.icon,image/png" type="file" onChange={(event) => setFile(event.target.files?.[0] ?? null)} /></label>
          <div className="form-actions"><button className="primary-button" disabled={!file || upload.isPending} type="submit"><Upload />上传并启用</button>{state.data.source === "DATABASE" && <button className="secondary-button" disabled={reset.isPending} type="button" onClick={() => { if (window.confirm("恢复 EvilEye 默认站点图标？")) reset.mutate(); }}><RotateCcw />恢复默认</button>}</div>
          <MutationMessage error={upload.error ?? reset.error} success={(upload.isSuccess && "站点图标已更新") || (reset.isSuccess && "已恢复默认图标")} />
        </form>
      </div>
    </Section>}
  </>;
}

function updateDocumentFavicon(url: string) {
  let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
  if (!link) {
    link = document.createElement("link");
    link.rel = "icon";
    document.head.appendChild(link);
  }
  link.href = url;
}

function formatBytes(value: number) {
  return value < 1024 ? `${value} B` : `${(value / 1024).toFixed(1)} KiB`;
}
